const systemImpacts = [
  {
    icon: 'ri-heart-pulse-line',
    label: 'Heart',
    bg: 'bg-red-50',
    iconColor: 'text-red-500',
    border: 'border-red-100',
    description:
      'Valvular defects, septal abnormalities, bicuspid aortic valve, and in rare cases dilated cardiomyopathy. Cardiac involvement is one of the most critical aspects of CSCF management.',
  },
  {
    icon: 'ri-body-scan-line',
    label: 'Skeletal System',
    bg: 'bg-brand-50',
    iconColor: 'text-brand-500',
    border: 'border-brand-100',
    description:
      'Vertebral fusions in the cervical and thoracic spine, carpal and tarsal bone fusion, brachydactyly, scoliosis, short stature, and joint hypermobility are commonly reported.',
  },
  {
    icon: 'ri-volume-mute-line',
    label: 'Hearing',
    bg: 'bg-accent-50',
    iconColor: 'text-accent-500',
    border: 'border-accent-100',
    description:
      'Bilateral conductive hearing loss is present in most cases, often due to inner ear malformation or stenosis of the external auditory canal. Early audiological intervention is key.',
  },
  {
    icon: 'ri-brain-line',
    label: 'Development',
    bg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
    border: 'border-emerald-100',
    description:
      'Mild global developmental delay, speech delay, and hypotonia are frequently observed. Most children benefit significantly from early intervention therapies.',
  },
  {
    icon: 'ri-emotion-line',
    label: 'Craniofacial',
    bg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    border: 'border-amber-100',
    description:
      'Characteristic facial features include a prominent forehead, widely spaced eyes, downslanted palpebral fissures, broad nasal bridge, and low-set ears.',
  },
  {
    icon: 'ri-restaurant-line',
    label: 'Gastrointestinal',
    bg: 'bg-orange-50',
    iconColor: 'text-orange-500',
    border: 'border-orange-100',
    description:
      'Feeding difficulties, gastroesophageal reflux, and gastrointestinal dysmotility are common, particularly in infancy. Some children require G-tube feeding.',
  },
];

export default function AboutContent() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">

        {/* 2-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">

          {/* LEFT: Text explanation */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 font-heading">
                A Rare Genetic Condition
              </h2>
              <p className="text-gray-600 leading-relaxed font-body mb-4">
                Cardiospondylocarpofacial syndrome (CSCF, also written CSCFS) is caused by heterozygous pathogenic variants in the <strong className="text-gray-800">MAP3K7</strong> gene, located on chromosome 6q15. This gene encodes TAK1 — a protein kinase critical to embryonic development and multiple cellular signaling pathways.
              </p>
              <p className="text-gray-600 leading-relaxed font-body">
                CSCF follows an <strong className="text-gray-800">autosomal dominant</strong> inheritance pattern, meaning a single altered copy of the gene is sufficient to cause the condition. In most reported cases, the variant is <em>de novo</em> — meaning it arose spontaneously and was not inherited from either parent.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 space-y-4">
              <h3 className="text-base font-bold text-gray-900 font-heading flex items-center gap-2">
                <i className="ri-test-tube-line text-brand-500"></i>
                Genetic Profile
              </h3>
              {[
                { label: 'Gene', value: 'MAP3K7' },
                { label: 'Chromosome', value: '6q15' },
                { label: 'Protein', value: 'TAK1 (TGF-β-activated kinase 1)' },
                { label: 'Inheritance', value: 'Autosomal dominant' },
                { label: 'Variant type', value: 'Heterozygous (usually de novo)' },
                { label: 'OMIM', value: '#157800' },
              ].map((row) => (
                <div key={row.label} className="flex items-start gap-3">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5 font-heading">
                    {row.label}
                  </span>
                  <span className="text-sm text-gray-700 font-body">{row.value}</span>
                </div>
              ))}
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 font-heading">Multi-System Impact</h3>
              <p className="text-gray-600 leading-relaxed font-body mb-4">
                Because TAK1 is involved in so many cellular processes, variants in MAP3K7 can affect multiple organ systems simultaneously. This is what makes CSCF both complex to manage and challenging to diagnose — no two individuals present identically.
              </p>
              <p className="text-gray-600 leading-relaxed font-body">
                Fewer than 30 cases have been genetically confirmed in medical literature worldwide as of 2025, making CSCF one of the rarest genetic conditions known. Each new case adds to the collective understanding of this condition.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/science"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-500 text-white font-semibold rounded-full hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
              >
                <i className="ri-microscope-line"></i>
                View Full Research
              </a>
              <a
                href="https://www.facebook.com/share/g/1BT8NGBZVX/?mibextid=K35XfP"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-brand-300 hover:text-brand-600 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
              >
                <i className="ri-group-line"></i>
                Join the Community
              </a>
            </div>
          </div>

          {/* RIGHT: Icon bullet list */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 font-heading">
              Systems Affected by CSCF
            </h2>
            {systemImpacts.map((item) => (
              <div
                key={item.label}
                className={`flex items-start gap-4 p-5 rounded-2xl border ${item.bg} ${item.border}`}
              >
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 bg-white`}>
                  <i className={`${item.icon} text-xl ${item.iconColor}`}></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1 font-heading text-sm">{item.label}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed font-body">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Highlight box — 2 column with photo */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-500 to-accent-600">
          {/* Decorative circles */}
          <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/10 pointer-events-none" />
          <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full bg-white/10 pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 items-stretch">
            {/* LEFT: Photo */}
            <div className="w-full h-72 lg:h-auto min-h-[320px] overflow-hidden rounded-t-3xl lg:rounded-t-none lg:rounded-l-3xl flex-shrink-0">
              <img
                src="https://storage.readdy-site.link/project_files/c4084cf6-f9fc-491d-816c-98ce35896c67/1583d58e-de52-49d3-a37b-8e2ce815d687_IMG_9960.jpg"
                alt="Child with Cardiospondylocarpofacial syndrome CSCF — family living with a rare genetic condition"
                title="CSCF family — Cardiospondylocarpofacial syndrome community"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* RIGHT: Text content */}
            <div className="p-10 lg:p-14 flex flex-col justify-center">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-user-heart-line text-white text-2xl"></i>
              </div>
              <blockquote className="text-2xl md:text-3xl font-bold text-white leading-snug mb-4 font-heading">
                &ldquo;Every case is different. No two individuals experience CSCF the same way.&rdquo;
              </blockquote>
              <p className="text-white/75 text-base font-body leading-relaxed mb-8">
                This variability is why personalized care, specialist coordination, and community connection are so essential for every family navigating this diagnosis.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/science#cases"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-brand-600 font-bold rounded-full hover:bg-brand-50 transition-colors whitespace-nowrap cursor-pointer font-body"
                >
                  <i className="ri-file-paper-2-line"></i>
                  Read Case Studies
                </a>
                <a
                  href="https://www.facebook.com/share/g/1BT8NGBZVX/?mibextid=K35XfP"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/15 border-2 border-white/40 text-white font-semibold rounded-full hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer font-body"
                >
                  <i className="ri-group-line"></i>
                  Connect with Families
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
